import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { apiClient } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem("eco_token") || null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(!!localStorage.getItem("eco_token"));

  const applyToken = useCallback((t) => {
    if (t) {
      localStorage.setItem("eco_token", t);
      apiClient.defaults.headers.common["Authorization"] = `Bearer ${t}`;
    } else {
      localStorage.removeItem("eco_token");
      delete apiClient.defaults.headers.common["Authorization"];
    }
    setToken(t);
  }, []);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/auth/me");
      setUser(data);
    } catch (err) {
      console.warn("fetchMe failed", err);
      applyToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [applyToken]);

  useEffect(() => {
    if (token) {
      apiClient.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchMe();
    }
  }, [token, fetchMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await apiClient.post("/auth/login", { email, password });
    applyToken(data.token);
    setUser(data.user);
    return data.user;
  }, [applyToken]);

  const logout = useCallback(() => {
    applyToken(null);
    setUser(null);
  }, [applyToken]);

  const value = useMemo(
    () => ({ token, user, loading, login, logout, refresh: fetchMe }),
    [token, user, loading, login, logout, fetchMe]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
